import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  afterNextRender,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { TextComponent } from '@app/components/text/text.component';
import { setTimeout$ } from '@app/helpers/timeout';
import { Media } from '@app/models/media.model';
import { ComponentSharedModule } from '@app/modules/component.shared.module';
import { BreakpointService } from '@app/services/breakpoint.service';
import { MobiUiMapService } from '@mobi/rwc-ui-components-ng-jslib';
import { distinctUntilChanged, lastValueFrom, map, of, switchMap } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TealiumUtagService } from '@app/services/utag.service';
import { Location, SearchTerm } from '@app/models/global.model';
import { SearchFieldComponent } from './search-field/search-field.component';
import { LocationListComponent } from './location-list/location-list.component';
import { LocationMapComponent } from './location-map/location-map.component';
import { ImageGalleryComponent } from './image-gallery/image-gallery.component';
import { MobiUiIconComponent } from '@mobi/rwc-ui-components-ng-jslib';
import { ApiService } from '@app/services/api.service';

@Component({
  selector: 'mobi-ga-search',
  templateUrl: './ga-search.component.html',
  styleUrls: ['./ga-search.component.scss'],
  standalone: true,
  imports: [
    ComponentSharedModule,
    SearchFieldComponent,
    LocationListComponent,
    LocationMapComponent,
    ImageGalleryComponent,
    TextComponent,
    MobiUiIconComponent,
  ],
})
export class GaSearchComponent {
  readonly searchFieldComponent = viewChild<SearchFieldComponent>(SearchFieldComponent);

  readonly showMap = input(true);
  readonly lead = input('');
  readonly images = input<Media[]>([]);

  #map = inject(MobiUiMapService);
  #http = inject(HttpClient);
  #api = inject(ApiService);
  #document = inject(DOCUMENT);
  #tealium = inject(TealiumUtagService);
  breakpoint = inject(BreakpointService);

  Marker!: any;
  locations = signal<Location[]>([]);
  filteredLocations = signal<Location[]>([]);
  activeLocation = signal<Location | null>(null);
  searchTerm = signal('');
  loading = signal(false);
  hasPagination = signal(false);
  pageCount = signal(1);
  #mapsLibraryLoaded = signal(false);

  #searchTerms: SearchTerm[] = [];

  searchMatches = toSignal(
    toObservable(this.searchTerm).pipe(
      distinctUntilChanged(),
      switchMap((searchTerm: string) => {
        if (!searchTerm) return of([]);
        const formattedSearchTerm = searchTerm.toLocaleLowerCase();
        const specialPrefixes = ['generalagentur', 'agence générale', 'agenzia generale'];
        const categoryOrder = ['location', 'general_agency'];
        let terms: SearchTerm[] = this.#searchTerms.filter((term) =>
          term.label.toLocaleLowerCase().includes(formattedSearchTerm)
        );

        // Helper function to remove special prefixes for sorting purposes
        const removeSpecialPrefix = (label: string) => {
          for (const prefix of specialPrefixes) {
            if (label.toLowerCase().startsWith(prefix.toLowerCase())) {
              return label.slice(prefix.length).trim();
            }
          }
          return label;
        };

        // Sort by category order
        terms = terms.sort((a, b) => {
          const categoryA = categoryOrder.indexOf(a.category);
          const categoryB = categoryOrder.indexOf(b.category);

          if (categoryA !== -1 && categoryB !== -1) {
            return categoryA - categoryB;
          }
          if (categoryA !== -1) return -1;
          if (categoryB !== -1) return 1;
          return 0;
        });

        // Sort by starts with in order to get starts with matches on top
        terms = terms.sort((a, b) => {
          const formattedLabelA = removeSpecialPrefix(a.label.toLocaleLowerCase());
          const formattedLabelB = removeSpecialPrefix(b.label.toLocaleLowerCase());

          const startsWithSearchTermA = formattedLabelA
            .toLowerCase()
            .startsWith(formattedSearchTerm.toLowerCase());
          const startsWithSearchTermB = formattedLabelB
            .toLowerCase()
            .startsWith(formattedSearchTerm.toLowerCase());

          if (startsWithSearchTermA && !startsWithSearchTermB) return -1;
          if (!startsWithSearchTermA && startsWithSearchTermB) return 1;
          return 0;
        });

        terms = terms.filter((term) => !!term.gaId || !!term.value);
        return of(terms);
      })
    ),
    { initialValue: [] }
  );

  #afterRender = afterNextRender({
    write: async () => {
      const rawLocations = await lastValueFrom(
        this.#http
          .get(`${this.#api.apiUrl}/api/v1/map/agencies`, {
            withCredentials: this.#api.loggedIn(),
          })
          .pipe(
            map((response: any) => {
              // Return raw location data without markers initially
              return response.agencies.data;
            })
          )
      );

      // Build a map of gaId -> main agency phone number
      const mainAgencyPhoneMap = new Map<string, string>();
      for (const loc of rawLocations) {
        if (loc.isMain && loc.phoneNumber) {
          mainAgencyPhoneMap.set(loc.gaId, loc.phoneNumber);
        }
      }
      // Enrich each location with the main agency's phone number
      for (const loc of rawLocations) {
        loc.gaPhoneNumber = mainAgencyPhoneMap.get(loc.gaId) ?? loc.phoneNumber;
      }

      this.locations.set(rawLocations);

      this.#searchTerms = await lastValueFrom(
        this.#http.get<SearchTerm[]>(`${this.#api.apiUrl}/api/v1/map/searchterms`)
      );

      if (!this.showMap()) return;
      const { Marker } = await this.#map.importLibrary('marker');
      this.Marker = Marker;
      this.#mapsLibraryLoaded.set(true);
      const locationsWithMarkers = this.#addMarkersToLocations(this.locations());
      this.locations.set(locationsWithMarkers);
    },
  });

  #addMarkersToLocations(locations: Location[]): Location[] {
    if (!this.#mapsLibraryLoaded()) return locations;
    return locations.map((location: Location) => {
      const marker = new this.Marker({
        position: location.coordinates,
        icon: {
          url: '/marker-dot.svg',
          scaledSize: new google.maps.Size(15, 15),
          origin: new google.maps.Point(0, 0),
          anchor: new google.maps.Point(7.5, 15),
        },
      });

      marker.addListener('click', () => {
        this.onLocationSelected(location);
      });

      location.marker = marker;
      return location;
    });
  }

  // Event handlers for subcomponents
  onLocationSelected(location: Location): void {
    // Add location to the filtered locations if not present
    let found: boolean = false;
    this.filteredLocations().forEach((fLocation: Location) => {
      if (location?.locationId === fLocation.locationId) found = true;
    });

    if (!found) {
      this.filteredLocations.set([location]);
      if (this.searchFieldComponent()) {
        this.searchFieldComponent()!.typeahead()!.inputElement()!.nativeElement.value =
          '';
      }
    }

    if (this.showMap()) {
      this.setActiveLocation(location);
      const activeLocation = this.#document.querySelector('.active-location');
      const locationsWrap = this.#document.querySelector('.locations-wrap');
      const activeLocationElement = activeLocation as HTMLElement;
      const locationsWrapElement = locationsWrap as HTMLElement;
      const activeLocationOffset =
        activeLocationElement?.offsetTop - locationsWrapElement?.offsetTop;
      if (locationsWrapElement) locationsWrapElement.scrollTop = activeLocationOffset;
    }

    if (this.breakpoint.xsMax()) {
      this.#document
        .querySelector(`*[data-location="${location.locationId}"]`)
        ?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
    }
  }

  setActiveLocation(location: Location): void {
    this.activeLocation.set(location);
  }

  resetActiveMarker(): void {
    this.activeLocation.set(null);
  }

  /**
   * Handle search input changes
   */
  #debounceTimer: any;

  async searchTermChange(value: string): Promise<void> {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => {
      this.searchTerm.set(value);
    }, 250);
  }

  /**
   * Handle search term selection from typeahead
   */
  async valueChanges(label: string): Promise<void> {
    const term = this.searchMatches().find((match) => match.label === label);

    if (!term?.label) {
      this.searchTerm.set('');
      this.resetActiveMarker();
      this.filteredLocations.set([]);
      this.pageCount.set(1);
      this.hasPagination.set(false);
      if (this.searchFieldComponent()) {
        this.searchFieldComponent()!.typeahead()!.inputElement()!.nativeElement.value =
          '';
      }
      return;
    }

    this.loading.set(true);
    if (!term.value) term.value = term.gaId;
    const params: string = `${term.value}/${term.label.split('/')[0]}`;
    const filter: string[] = await lastValueFrom(
      this.#http.get<string[]>(`${this.#api.apiUrl}/api/v1/map/search/${params}`, {
        withCredentials: this.#api.loggedIn(),
      })
    );

    this.filteredLocations.set([]);
    this.locations().forEach((location: Location) => {
      if (location && filter.includes(location?.locationId)) {
        const index = filter.indexOf(location.locationId);
        this.filteredLocations()[index] = location;
      }
    });

    this.pageCount.set(1);
    this.hasPagination.set(this.filteredLocations().length > 3);
    this.searchTerm.set(term.label);
    this.#tealium.link({
      tealium_event: 'interaction_event',
      interaction_type: 'search',
      element_type: 'input',
      element_name: 'search agency',
      element_value: term.label.trim(),
    });

    if (this.showMap() && this.breakpoint.smAndLarger()) {
      this.resetActiveMarker();
      if (this.filteredLocations().length > 0) {
        setTimeout$(() => {
          this.setActiveLocation(this.filteredLocations()[0]);
        }, 5);
      }
    }
    this.loading.set(false);
  }

  loadNext(): void {
    this.pageCount.update((c) => c + 1);
    const hasPagination = this.filteredLocations().length / 3 > this.pageCount();
    this.hasPagination.set(hasPagination);
  }
}
